import { rasterSrc } from "@/generated/raster-images";
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, Section } from "@/components/site-blocks";
import { EnquiryForm } from "@/components/enquiry-form";
import { EmailContactLink } from "@/components/email-contact-link";
import { SITE } from "@/lib/site";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  head: () => ({
    ...pageSeo({
      path: "/contact",
      title: "Contact CRS Energy | Bridgend | 0800 009 2992",
      description:
        "Contact Complete Remedial Solutions in Bridgend. Phone 0800 009 2992 or send a message. Lines open 24/7 across Wales and the South West.",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact CRS Energy",
          url: `${SITE.url}/contact`,
          mainEntity: { "@id": `${SITE.url}/#localbusiness` },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Get in touch"
        description="Free survey, fixed-price quote, or emergency support - lines open 24/7. Talk to the team today."
        image={rasterSrc("stock/photo-1497366216548-37526070297c")}
        imageAlt="Office workspace"
      />
      <Section>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <EnquiryForm formType="contact" variant="contact" sourcePage="/contact" title="Send us a message" submitLabel="Send message" />
          <div className="space-y-6">
            <div className="rounded-2xl border bg-card p-6">
              <h3 className="font-semibold mb-4">Contact details</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <Phone className="h-5 w-5 text-accent shrink-0" aria-hidden />
                  <span className="flex flex-col">
                    <a href={SITE.phoneTel} className="hover:text-accent">{SITE.phone}</a>
                    <a href={SITE.phoneSecondaryTel} className="hover:text-accent">{SITE.phoneSecondary}</a>
                  </span>
                </li>
                <li className="flex gap-3">
                  <Mail className="h-5 w-5 text-accent shrink-0" aria-hidden />
                  <EmailContactLink className="hover:text-accent underline underline-offset-2 break-all" />
                </li>
                <li className="flex gap-3">
                  <MapPin className="h-5 w-5 text-accent shrink-0 mt-0.5" aria-hidden />
                  <address className="not-italic">
                    {SITE.address.line1}<br />
                    {SITE.address.line2}<br />
                    {SITE.address.city}, {SITE.address.postcode}
                  </address>
                </li>
                <li className="flex gap-3">
                  <Clock className="h-5 w-5 text-accent shrink-0" aria-hidden />
                  <span>Mon-Fri 8am-6pm · Lines open 24/7</span>
                </li>
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden border bg-card">
              <iframe
                title="CRS Bridgend HQ map"
                src="https://www.google.com/maps?q=Bridgend+Industrial+Estate,+Bridgend+CF31+3TQ&output=embed"
                className="w-full h-72"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
